import React from 'react';
import { Box, Typography, TextField, Paper, Stack } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';

interface InitialsDisplayProps {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  guessState: {
    firstName: string;
    middleName: string;
    lastName: string;
    gameCompleted: boolean;
  };
  onNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isMobile: boolean;
}

const InitialsDisplay: React.FC<InitialsDisplayProps> = ({
  firstName,
  middleName,
  lastName,
  guessState,
  onNameChange,
  isMobile
}) => {
  const renderWithMiddleName = () => (
    isMobile ? (
      <Paper elevation={3} sx={{ px: { xs: 0.5, sm: 0.4, md: 1 }, py: { xs: 0.5, sm: 0.4, md: 1 }, mb: 2, width: '100%', maxWidth: { xs: '100%', md: 500 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 1, sm: 1.5, md: 2 } }}>
          <Typography
            variant="h3"
            component="h1"
            color="primary"
            gutterBottom
            sx={{
              mb: 0.5,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
            }}
          >
            {firstName?.charAt(0)} {middleName?.charAt(0)} {lastName?.charAt(0)}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 0.25,
              fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.875rem' }
            }}
          >
            Enter the full name for these initials
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
          >
            💡 Tip: Check your spelling.
          </Typography>
        </Box>
        <Stack spacing={{ xs: 0.75, sm: 1, md: 1.25 }}>
          <TextField
            id="first-input"
            name="firstName"
            label="First Name"
            variant="outlined"
            fullWidth
            size="small"
            value={guessState.firstName}
            onChange={onNameChange}
            disabled={guessState.gameCompleted}
          />
          <TextField
            id="middle-input"
            name="middleName"
            label="Middle Name"
            variant="outlined"
            fullWidth
            size="small"
            value={guessState.middleName}
            onChange={onNameChange}
            disabled={guessState.gameCompleted}
          />
          <TextField
            id="last-input"
            name="lastName"
            label="Last Name"
            variant="outlined"
            fullWidth
            size="small"
            value={guessState.lastName}
            onChange={onNameChange}
            disabled={guessState.gameCompleted}
          />
        </Stack>
      </Paper>
    ) : (
      <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 2 }}>
          Enter the full name for these initials
        </Typography>
        <Typography variant="caption" color="text.secondary" align="center" sx={{ mb: 3, display: 'block' }}>
          💡 Tip: Check your spelling.
        </Typography>
        <Typography variant="h1" component="div" color="primary" align="center" sx={{ mb: 2, fontWeight: 'bold' }}>
          {firstName?.charAt(0)}
          {middleName?.charAt(0)}
          {lastName?.charAt(0)}
        </Typography>
        <Grid container spacing={3}>
          <Grid sm={4}>
            <Box sx={{
              textAlign: 'center',
              p: 2,
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              backgroundColor: 'background.paper'
            }}>
              <TextField
                id="first-input"
                name="firstName"
                label="First Name"
                variant="outlined"
                fullWidth
                size="small"
                value={guessState.firstName}
                onChange={onNameChange}
                disabled={guessState.gameCompleted}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
              />
            </Box>
          </Grid>
          <Grid sm={4}>
            <Box sx={{
              textAlign: 'center',
              p: 2,
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              backgroundColor: 'background.paper'
            }}>
              <TextField
                id="middle-input"
                name="middleName"
                label="Middle Name"
                variant="outlined"
                fullWidth
                size="small"
                value={guessState.middleName}
                onChange={onNameChange}
                disabled={guessState.gameCompleted}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
              />
            </Box>
          </Grid>
          <Grid sm={4}>
            <Box sx={{
              textAlign: 'center',
              p: 2,
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              backgroundColor: 'background.paper'
            }}>
              <TextField
                id="last-input"
                name="lastName"
                label="Last Name"
                variant="outlined"
                fullWidth
                size="small"
                value={guessState.lastName}
                onChange={onNameChange}
                disabled={guessState.gameCompleted}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>
    )
  );

  const renderWithoutMiddleName = () => (
    <Paper elevation={3} sx={{ px: { xs: 0.5, sm: 0.4, md: 1, lg: 3 }, py: { xs: 0.5, sm: 0.4, md: 1, lg: 3 }, mb: 2 }}>
      <Typography
        variant="body1"
        color="text.secondary"
        align="center"
        sx={{
          mb: { xs: 0.5, sm: 1, md: 1.5, lg: 2 },
          fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem', lg: '1rem' }
        }}
      >
        Enter the full name for these initials
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        align="center"
        sx={{
          mb: { xs: 1, sm: 1.5, md: 2, lg: 3 },
          display: 'block',
          fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }
        }}
      >
        💡 Tip: Check your spelling.
      </Typography>
      <Typography
        variant="h1"
        component="div"
        color="primary"
        align="center"
        sx={{
          mb: { xs: 1, sm: 1.5, md: 2 },
          fontWeight: 'bold',
          fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' }
        }}
      >
        {firstName?.charAt(0)}
        {lastName?.charAt(0)}
      </Typography>

      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 0.75, sm: 1, md: 2, lg: 3 },
        justifyContent: 'center'
      }}>
        <Box sx={{
          textAlign: 'center',
          px: { xs: 0.5, sm: 0.4, lg: 2 },
          py: { xs: 0.5, sm: 0.4, lg: 2 },
          backgroundColor: 'background.paper',
          flex: { xs: 'none', md: 1 },
          maxWidth: { xs: '100%', md: '300px' }
        }}>
          <TextField
            id="first-input"
            name="firstName"
            label="First Name"
            variant="outlined"
            fullWidth
            size="small"
            value={guessState.firstName}
            onChange={onNameChange}
            disabled={guessState.gameCompleted}
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
          />
        </Box>
        <Box sx={{
          textAlign: 'center',
          px: { xs: 0.5, sm: 0.4, lg: 2 },
          py: { xs: 0.5, sm: 0.4, lg: 2 },
          backgroundColor: 'background.paper',
          flex: { xs: 'none', md: 1 },
          maxWidth: { xs: '100%', md: '300px' }
        }}>
          <TextField
            id="last-input"
            name="lastName"
            label="Last Name"
            variant="outlined"
            fullWidth
            size="small"
            value={guessState.lastName}
            onChange={onNameChange}
            disabled={guessState.gameCompleted}
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
          />
        </Box>
      </Box>
    </Paper>
  );

  return middleName ? renderWithMiddleName() : renderWithoutMiddleName();
};

export default InitialsDisplay;